/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import type AssessmentType from '@linc-technologies/linced-common/enums/assessment-type';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Category from '@linc-technologies/linced-common/models/assessments/category';
import type GradeSchedule from '@linc-technologies/linced-common/models/assessments/grade-schedule';
import type Module from '@linc-technologies/linced-common/models/assessments/module';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

// Proto: assessments.Assessment.
export default class Assessment extends Model {
  declare [Type]: 'assessments/assessment';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('string', { defaultValue: '' })
  declare templateId: string; // TODO: implement once Templates are added

  @belongsTo('assessments/category', { async: true, inverse: 'assessments' })
  declare categoryId: AsyncBelongsTo<Category>;

  @alias('categoryId')
  declare category: AsyncBelongsTo<Category>;

  @alias('category.id')
  declare category_id: string | null | undefined;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @hasMany('assessments/module', { async: true, inverse: 'assessmentId' })
  declare modules: AsyncHasMany<Module>;

  @computed('modules.content.[]')
  get modulesArray() {
    return resolved(this.modules);
  }

  moduleSortOrder = Object.freeze(['sequence', 'label']);

  @sort('modulesArray', 'moduleSortOrder')
  declare sortedModules: unknown[];

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof AssessmentType> | 0;

  @attr('number', { defaultValue: 0 })
  declare assessmentDisplay: EnumValue<typeof AssessmentDisplay> | 0;

  @attr('string', { defaultValue: '' })
  declare key: string;

  @belongsTo('assessments/grade-schedule', { async: true, inverse: null })
  declare gradeScheduleId: AsyncBelongsTo<GradeSchedule>;

  @alias('gradeScheduleId')
  declare gradeSchedule: AsyncBelongsTo<GradeSchedule>;

  @alias('gradeSchedule.id')
  declare gradeSchedule_id: string | null | undefined;

  @attr('boolean', { defaultValue: false })
  declare isModerated: boolean;

  @attr('number', { defaultValue: 0 })
  declare numericStepping: number;

  @attr('number', { defaultValue: 0 })
  declare dueTime: number;

  @attr('boolean', { defaultValue: false })
  declare isVisibleToLearners: boolean;

  @attr('boolean', { defaultValue: false })
  declare canLearnersEdit: boolean;

  @attr('boolean', { defaultValue: false })
  declare showHistoryToLearners: boolean;

  @attr('number', { defaultValue: 0 })
  declare noGradesColumnsToShowCaregiversStudents: number;

  @attr('number', { defaultValue: 0 })
  declare noGradesColumnsToShowStaff: number;

  @attr('boolean', { defaultValue: false })
  declare showToCaregiversOnlyIfContent: boolean;
}
